import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  EngineProvider,
  LensLoadError,
  createLensHostUriPlugin,
  LensCanvas,
  installLensHostPostMessage,
  installLensHostWebSocket,
  lensProxyUrl,
  precacheAssetUrlResolver,
  lensLoadErrorPresentation,
  useCameras,
  useEngine,
  useHostBridge,
  useLensHostStatus,
} from "@lenscore-pipeline/lens-canvas";

const rendererPlugins = [createLensHostUriPlugin()];

// Auto-install a lensHost when nothing else has set one. Electron preload
// and native CEF V8 bindings set `window.lensHost` before this module runs
// and we leave those alone. Otherwise:
//
//   ?room=<id>  → installLensHostWebSocket against /control/<id> on this
//                 origin. Lets multiple browsers in different processes
//                 (or one TD/CEF + one browser) coordinate via a Durable
//                 Object room. Optional ?id=<rendererId> for stable ids.
//   (no room)   → installLensHostPostMessage(). Iframe parents and
//                 TouchDesigner Web Render TOP (via window.lensCommand)
//                 get a default control surface with zero host-side setup;
//                 plain top-level tabs are silently self-targeted.
if (typeof window !== "undefined" && !window.lensHost) {
  const qs = new URLSearchParams(window.location.search);
  const room = qs.get("room");
  if (room) {
    const wsScheme = window.location.protocol === "https:" ? "wss" : "ws";
    const wsUrl = `${wsScheme}://${window.location.host}/control/${encodeURIComponent(room)}`;
    installLensHostWebSocket({
      url: wsUrl,
      role: "renderer",
      id: qs.get("id") ?? undefined,
    });
  } else {
    installLensHostPostMessage();
  }
}

const PRECACHE_ORIGIN = import.meta.env.VITE_LENSCORE_PRECACHE_ORIGIN ?? "https://search-lens.byteo.app";

const CAMERA_STORAGE_KEY = "lens-renderer:cameraDeviceId";
const VALID_FIT_VALUES = new Set(["contain", "cover", "fill"]);

function parseQuery() {
  if (typeof window === "undefined") return {};
  const p = new URL(window.location.href).searchParams;
  const fit = p.get("fit") || "";
  return {
    lens: p.get("lens") || "",
    image: p.get("image") || "",
    camera: p.get("camera") || "",
    cameraId: p.get("cameraId") || "",
    cameraOrientation: p.get("cameraOrientation") || "",
    outputOrientation: p.get("outputOrientation") || "",
    debug: p.get("debug") === "1" || p.get("debug") === "true",
    chromeHidden: (p.get("chrome") || "").toLowerCase() === "hidden",
    headless: p.get("headless") === "1" || p.get("headless") === "true",
    fit: VALID_FIT_VALUES.has(fit) ? fit : "contain",
  };
}

export default function App() {
  const deviceClass = typeof window === "undefined"
    ? undefined
    : new URL(window.location.href).searchParams.get("deviceClass") || undefined;
  const engineOptions = useMemo(() => ({
    logger: (msg, detail) => {
      console.debug("[lens-engine]", msg, detail);
      window.lensHost?.log?.("info", String(msg || "engine"), detail);
    },
    onError: (err) => {
      console.error("[lens-engine] error", err);
      window.lensHost?.log?.("error", "engine error", {
        message: err?.message || String(err),
        stack: err?.stack || null,
      });
    },
    resolveRemoteAssetUrl: precacheAssetUrlResolver(PRECACHE_ORIGIN),
    rewriteFetchUrl: lensProxyUrl,
    // Keep both visible Preview and native off-screen CEF on rAF. A live
    // Windows A/B test on 2026-09-02 showed that LensCore's SetTimeout mode
    // lets CEF paint its initial static frame but then prevents the camera
    // render loop from advancing during Preview -> Virtual handoff.
    renderLoopMode: "requestAnimationFrame",
    deviceClass,
  }), [deviceClass]);

  return (
    <EngineProvider options={engineOptions} plugins={rendererPlugins}>
      <Renderer />
    </EngineProvider>
  );
}

function Renderer() {
  const cameras = useCameras();
  const { ready, actions, error } = useEngine();
  const query = useMemo(parseQuery, []);

  // Bridges `window.lensHost` (when present) to the engine — host shells can
  // drive the renderer headlessly. No-op in plain browser tabs.
  useHostBridge();

  const [cameraId, setCameraId] = useState(() => {
    if (query.cameraId) return query.cameraId;
    try { return window.localStorage.getItem(CAMERA_STORAGE_KEY) || ""; } catch { return ""; }
  });
  const [showDebug, setShowDebug] = useState(query.debug);
  const [lensInput, setLensInput] = useState(query.lens);
  const [lensError, setLensError] = useState(null);
  useEffect(() => {
    try { window.localStorage.setItem(CAMERA_STORAGE_KEY, cameraId); } catch {
      // ignore storage failures (third-party cookie blocking, private browsing, etc.)
    }
  }, [cameraId]);

  // Resolve ?camera=<label-substring> against enumerated devices once they arrive.
  // Skipped when an explicit ?cameraId= or localStorage value already selected one.
  const labelMatchedRef = useRef(false);
  useEffect(() => {
    if (labelMatchedRef.current) return;
    if (cameraId) return;
    if (!query.camera) return;
    if (!cameras.length) return;
    const needle = query.camera.toLowerCase();
    const match = cameras.find((c) => (c.label || "").toLowerCase().includes(needle));
    if (match) {
      setCameraId(match.deviceId);
      labelMatchedRef.current = true;
    }
  }, [cameras, cameraId, query.camera]);

  const cameraConfig = useMemo(() => (query.headless ? undefined : ({
    deviceId: cameraId || undefined,
    orientation: query.cameraOrientation || undefined,
  })), [cameraId, query.cameraOrientation, query.headless]);

  // Auto-load the lens from `?lens=...` once the engine is ready.
  useEffect(() => {
    if (!ready) return;
    if (!query.lens) return;
    actions.loadLens(query.lens).catch(setLensError);
  }, [ready, actions, query.lens]);

  const handleLoadLens = useCallback((url) => {
    const trimmed = String(url || "").trim();
    if (!trimmed) return;
    setLensError(null);
    actions.loadLens(trimmed).catch(setLensError);
  }, [actions]);

  const { status: hostStatus, fatalReason: hostFatalReason } = useLensHostStatus();

  return (
    <div className="renderer">
      <LensCanvas
        camera={query.image ? undefined : cameraConfig}
        image={query.image ? { url: query.image, orientation: query.cameraOrientation || undefined } : undefined}
        fit={query.fit}
        showDebugOverlay={showDebug}
        outputOrientation={query.outputOrientation || undefined}
      />
      {hostFatalReason && (
        <ConnectionError reason={hostFatalReason} />
      )}
      {!hostFatalReason && hostStatus === "closed" && (
        <ConnectionStatus message="Reconnecting…" />
      )}
      {!query.chromeHidden && (
        <Chrome
          cameras={cameras}
          cameraId={cameraId}
          onCameraChange={setCameraId}
          showDebug={showDebug}
          onToggleDebug={() => setShowDebug((v) => !v)}
          lensInput={lensInput}
          onLensInputChange={setLensInput}
          onLoadLens={() => handleLoadLens(lensInput)}
          engineError={error || null}
          lensError={lensError}
        />
      )}
    </div>
  );
}

function Chrome({
  cameras,
  cameraId,
  onCameraChange,
  showDebug,
  onToggleDebug,
  lensInput,
  onLensInputChange,
  onLoadLens,
  engineError,
  lensError,
}) {
  const failure = lensError || engineError;
  const presentedError = !failure ? null : failure instanceof LensLoadError
    ? lensLoadErrorPresentation(failure)
    : {
      title: "LensCore needs attention",
      message: "The rendering engine reported an error.",
      code: "UNKNOWN",
      stage: "runtime",
      detail: failure?.message || String(failure),
    };
  return (
    <div className="chrome">
      <div className="chrome-row">
        <select value={cameraId} onChange={(event) => onCameraChange(event.target.value)}>
          <option value="">Default camera</option>
          {cameras.map((camera) => (
            <option key={camera.deviceId} value={camera.deviceId}>
              {camera.label}
            </option>
          ))}
        </select>
      </div>

      <div className="chrome-row">
        <input
          type="url"
          placeholder="lens URL (.lns)"
          value={lensInput}
          onChange={(event) => onLensInputChange(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter") onLoadLens(); }}
        />
        <button type="button" onClick={onLoadLens}>Load</button>
      </div>

      <div className="chrome-row">
        <label>
          <input type="checkbox" checked={showDebug} onChange={onToggleDebug} />
          Debug overlay
        </label>
      </div>

      {presentedError && (
        <div className="error">
          <strong>{presentedError.title}</strong>
          {`\n${presentedError.message}`}
          {`\n${presentedError.code} · ${presentedError.stage}`}
          {presentedError.detail && `\n${presentedError.detail}`}
        </div>
      )}
    </div>
  );
}

/**
 * Full-screen overlay shown when the lens-host adapter has hit a fatal
 * close. The user is offered a reload — the only meaningful recovery for
 * displace / policy / invalid-message closes.
 */
function ConnectionError({ reason }) {
  return (
    <div className="connection-error">
      <div className="connection-error-card">
        <h2>Control connection lost</h2>
        <p>{reason}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    </div>
  );
}

/**
 * Lightweight status banner shown when the WS adapter is mid-reconnect.
 * Non-blocking; sits in a corner so the canvas underneath stays visible.
 */
function ConnectionStatus({ message }) {
  return (
    <div className="connection-status" role="status">
      {message}
    </div>
  );
}
